import type { FastFeedPurchaseRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { fastFeedPurchasesQuery } from "@/queries/fast-feed";
import { useQuery } from "@tanstack/react-query";

const TH_STYLE: React.CSSProperties = {
  padding: "6px 10px",
  fontWeight: 500,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--color-text-muted)",
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--color-border)",
};

const TD_STYLE: React.CSSProperties = {
  padding: "7px 10px",
  fontSize: 12,
  borderBottom: "1px solid var(--color-border)",
  whiteSpace: "nowrap",
};

function shortAddr(addr: string) {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function shortHash(hash: string) {
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function formatTimestamp(ts: string) {
  const d = new Date(ts.replace(" ", "T").replace(".000", "Z"));
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function PurchaseRow({ row, rank }: { row: FastFeedPurchaseRow; rank: number }) {
  return (
    <tr>
      <td style={{ ...TD_STYLE, color: "var(--color-text-muted)", width: 36, textAlign: "center" }}>
        {rank}
      </td>
      <td
        style={{ ...TD_STYLE, fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}
      >
        {row.block_number.toLocaleString()}
      </td>
      <td style={{ ...TD_STYLE, color: "var(--color-text-muted)" }}>
        {formatTimestamp(row.block_timestamp)}
      </td>
      <td style={{ ...TD_STYLE, fontFamily: "var(--font-mono)" }}>
        <a
          href={`https://arbiscan.io/tx/${row.tx_hash}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--color-accent)", textDecoration: "none" }}
          title={row.tx_hash}
        >
          {shortHash(row.tx_hash)}
        </a>
      </td>
      <td style={{ ...TD_STYLE, fontFamily: "var(--font-mono)" }}>
        <a
          href={`https://arbiscan.io/address/${row.buyer_address}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--color-accent)", textDecoration: "none" }}
          title={row.buyer_address}
        >
          {shortAddr(row.buyer_address)}
        </a>
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {row.round_number.toLocaleString()}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {row.price_usdg.toFixed(2)}
      </td>
      <td style={{ ...TD_STYLE, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {row.num_tickets.toLocaleString()}
      </td>
      <td
        style={{
          ...TD_STYLE,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          color: "var(--color-text-primary)",
        }}
      >
        {row.revenue_usdg.toFixed(2)}
      </td>
    </tr>
  );
}

export function FastFeedPurchasesTable({ limit = 100 }: { limit?: number }) {
  const { data: rows = [], isLoading } = useQuery(fastFeedPurchasesQuery(limit));

  return (
    <Panel
      title="TICKET PURCHASE TRANSACTIONS"
      action={
        rows.length > 0 ? (
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
            Most recent {rows.length}
          </span>
        ) : undefined
      }
    >
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...TH_STYLE, textAlign: "center" }}>#</th>
              <th style={TH_STYLE}>Block</th>
              <th style={TH_STYLE}>Time (UTC)</th>
              <th style={TH_STYLE}>Tx Hash</th>
              <th style={TH_STYLE}>Buyer</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Round</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Price/Ticket (USDG)</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Tickets</th>
              <th style={{ ...TH_STYLE, textAlign: "right" }}>Total (USDG)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 8 }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
                  <tr key={i}>
                    {Array.from({ length: 9 }, (_, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cells
                      <td key={j} style={TD_STYLE}>
                        <div
                          className="skeleton"
                          style={{ height: 14, borderRadius: 2, width: "80%" }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row, i) => (
                  <PurchaseRow key={`${row.tx_hash}-${row.block_number}`} row={row} rank={i + 1} />
                ))}
          </tbody>
        </table>
        {!isLoading && rows.length === 0 && (
          <div
            style={{
              padding: "32px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            No ticket purchase data available
          </div>
        )}
      </div>
    </Panel>
  );
}
